import LogRocket from 'logrocket';
import type { AuthUser } from '@/models/auth.types';

// LogRocket configuration
const LOGROCKET_APP_ID = import.meta.env.VITE_LOGROCKET_APP_ID;

// Type for LogRocket initialization options
interface LogRocketConfig {
  sanitizeRequest?: (request: any) => any;
  sanitizeResponse?: (response: any) => any;
  network?: {
    requestSanitizer?: (request: any) => any;
    responseSanitizer?: (response: any) => any;
  };
  console?: {
    isEnabled?: boolean;
  };
  dom?: {
    isEnabled?: boolean;
  };
}

/**
 * Initialize LogRocket monitoring
 * Only initializes in production environment with valid app ID
 */
export const initializeLogRocket = (): void => {
  // Only initialize in production with a valid app ID
  if (!import.meta.env.PROD || !LOGROCKET_APP_ID) {
    console.log('LogRocket not initialized: Development mode or missing app ID');
    return;
  }

  try {
    // Extended configuration interface for CSS capture
    const config: LogRocketConfig & {
      baseHref?: string;
      dom?: {
        isEnabled?: boolean;
        inlineStylesheet?: boolean;
        textSanitizer?: boolean;
        inputSanitizer?: boolean;
      };
    } = {
      // Set baseHref to help LogRocket fetch CSS and assets
      // This tells LogRocket where to find resources when the app is served from a subdomain or CDN
      baseHref: window.location.origin,
      
      // Network configuration - no sanitization since we have nothing sensitive
      network: {
        // All network data will be captured without redaction
      },
      // Enable console logging
      console: {
        isEnabled: true,
      },
      // Enhanced DOM recording configuration for better CSS capture
      dom: {
        isEnabled: true,
        // Inline stylesheets to ensure CSS is captured in recordings
        inlineStylesheet: true,
        // Don't obfuscate text by default
        textSanitizer: false,
        // Don't sanitize input fields - show all field values
        inputSanitizer: false,
      },
    };

    LogRocket.init(LOGROCKET_APP_ID, config);
    console.log('LogRocket initialized successfully with CSS capture enabled');
  } catch (error) {
    console.error('Failed to initialize LogRocket:', error);
  }
};

/**
 * Identify a user in LogRocket
 * @param user - The authenticated user object
 */
export const identifyUser = (user: AuthUser): void => {
  // Only identify in production
  if (!import.meta.env.PROD || !LOGROCKET_APP_ID) {
    return;
  }

  try {
    const traits: Record<string, string> = {
      email: user.email,
      organizationId: user.organizationId,
      organizationName: user.organizationName,
      organizationSlug: user.organizationSlug,
      role: user.role,
    };
    
    // Only add name if it exists
    if (user.name) {
      traits.name = user.name;
    }
    
    LogRocket.identify(user.id, traits);
  } catch (error) {
    console.error('Failed to identify user in LogRocket:', error);
  }
};

/**
 * Track a custom event in LogRocket
 * @param eventName - Name of the event to track
 * @param metadata - Additional metadata for the event
 */
export const trackEvent = (eventName: string, metadata?: Record<string, any>): void => {
  // Only track in production
  if (!import.meta.env.PROD || !LOGROCKET_APP_ID) {
    return;
  }

  try {
    LogRocket.track(eventName, metadata);
  } catch (error) {
    console.error('Failed to track event in LogRocket:', error);
  }
};

/**
 * Capture an exception in LogRocket
 * @param error - The error to capture
 * @param metadata - Additional context about the error
 */
export const captureException = (error: Error, metadata?: Record<string, any>): void => {
  // Only capture in production
  if (!import.meta.env.PROD || !LOGROCKET_APP_ID) {
    return;
  }

  try {
    LogRocket.captureException(error, {
      extra: metadata,
    });
  } catch (err) {
    console.error('Failed to capture exception in LogRocket:', err);
  }
};

/**
 * Get the current LogRocket session URL
 * @returns The session URL or null if not available
 */
export const getSessionURL = (): string | null => {
  // Only available in production
  if (!import.meta.env.PROD || !LOGROCKET_APP_ID) {
    return null;
  }

  try {
    return LogRocket.sessionURL || null;
  } catch (error) {
    console.error('Failed to get LogRocket session URL:', error);
    return null;
  }
};

/**
 * Clear user identification (for logout)
 */
export const clearUserIdentification = (): void => {
  // Only clear in production
  if (!import.meta.env.PROD || !LOGROCKET_APP_ID) {
    return;
  }

  try {
    // LogRocket doesn't have a built-in clear method, but we can identify with anonymous user
    LogRocket.identify('anonymous', {});
  } catch (error) {
    console.error('Failed to clear user identification in LogRocket:', error);
  }
};

/**
 * Debug helper to verify CSS is being captured
 * Logs information about stylesheets to help diagnose CSS capture issues
 */
export const debugCSSCapture = (): void => {
  if (!import.meta.env.PROD || !LOGROCKET_APP_ID) {
    return;
  }
  
  try {
    // Log all stylesheets found in the document
    const stylesheets = Array.from(document.styleSheets);
    console.log(`[LogRocket CSS Debug] Found ${stylesheets.length} stylesheets`);
    
    let accessibleCount = 0;
    let crossOriginCount = 0;
    
    stylesheets.forEach((sheet, index) => {
      const href = sheet.href || 'inline';
      const isExternal = sheet.href && !sheet.href.startsWith(window.location.origin);
      
      try {
        // Try to access cssRules - this will throw for cross-origin stylesheets
        const rules = sheet.cssRules || sheet.rules;
        const ruleCount = rules ? rules.length : 0;
        accessibleCount++;
        
        console.log(`[LogRocket CSS Debug] Stylesheet ${index + 1}: ${href} (${ruleCount} rules, accessible)`);
        
        // Track accessible stylesheet info in LogRocket
        trackEvent('css_debug_accessible', {
          stylesheetIndex: index,
          href,
          ruleCount,
          isInline: !sheet.href,
          isExternal,
        });
      } catch (e) {
        // This is expected for cross-origin stylesheets (like Google Fonts)
        crossOriginCount++;
        
        // Only log as info, not warning, since this is expected behavior
        console.info(`[LogRocket CSS Debug] Stylesheet ${index + 1}: ${href} (cross-origin, not directly accessible)`);
        
        // Track cross-origin stylesheet (this is normal and expected)
        trackEvent('css_debug_cross_origin', {
          stylesheetIndex: index,
          href,
          isExternal,
          origin: sheet.href ? new URL(sheet.href).origin : 'inline',
        });
      }
    });
    
    console.log(`[LogRocket CSS Debug] Summary: ${accessibleCount} accessible, ${crossOriginCount} cross-origin stylesheets`);
    
    // Log information about how stylesheets are loaded
    console.log('[LogRocket CSS Debug] Note: Cross-origin stylesheets (like Google Fonts) cannot be accessed via JavaScript due to CORS policy.');
    console.log('[LogRocket CSS Debug] LogRocket captures these stylesheets server-side when proper CORS headers are present.');
    
    // Also log computed styles for a sample element to verify styles are applied
    const rootElement = document.getElementById('root');
    if (rootElement) {
      const computedStyles = window.getComputedStyle(rootElement);
      const fontFamily = computedStyles.fontFamily;
      
      console.log('[LogRocket CSS Debug] Root element computed styles:', {
        fontFamily,
        backgroundColor: computedStyles.backgroundColor,
        color: computedStyles.color,
        // Check if Google Fonts are applied
        hasGoogleFonts: fontFamily.includes('Inter Tight'),
      });
      
      // Track computed styles to verify CSS is applied
      trackEvent('css_debug_computed_styles', {
        fontFamily,
        hasGoogleFonts: fontFamily.includes('Inter Tight'),
        hasStyles: computedStyles.backgroundColor !== '' || computedStyles.color !== '',
      });
    }
  } catch (error) {
    console.error('[LogRocket CSS Debug] Failed to debug CSS capture:', error);
    trackEvent('css_debug_error', {
      error: error instanceof Error ? error.message : String(error),
      phase: 'general',
    });
  }
};

// Export LogRocket instance for direct access if needed
export default LogRocket;